import { CatalogPolicy } from "@/lib/catalog-api";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import type { DraftPolicy } from "../ConfigurationApp";
import { EditCoveragePath } from "./components/EditCoveragePath";

export type PendingCoveragePath = {
  key: string;
  draft: DraftPolicy;
};

function blankPolicy(draft: DraftPolicy): CatalogPolicy {
  return {
    escalationPolicyId: 0,
    tenantKey: "",
    code: draft.code,
    displayName: draft.displayName,
    isActive: draft.isActive,
    tiers: [],
  };
}

export interface CoveragePathProps {
  policies: CatalogPolicy[] | undefined;
  policyDraft: DraftPolicy;
  setPolicyDraft: Dispatch<SetStateAction<DraftPolicy>>;
  pendingPaths: PendingCoveragePath[];
  setPendingPaths: Dispatch<SetStateAction<PendingCoveragePath[]>>;
  onSelectPolicy: (
    policy: CatalogPolicy,
    patch?: { displayName: string; isActive: boolean },
  ) => void;
  policyWithEdits: (policy: CatalogPolicy) => CatalogPolicy;
  createNewDraft: (pendingKey: string) => DraftPolicy;
  onRememberPolicy: (
    policy: CatalogPolicy,
    values: { displayName: string; isActive: boolean },
  ) => void;
  onCommitActive?: (policyId: number, isActive: boolean) => void;
}

export const CoveragePath = ({
  policies,
  policyDraft,
  setPolicyDraft,
  pendingPaths,
  setPendingPaths,
  onSelectPolicy,
  policyWithEdits,
  createNewDraft,
  onRememberPolicy,
  onCommitActive,
}: CoveragePathProps) => {
  const list = policies ?? [];

  const rememberCurrentPending = (paths: PendingCoveragePath[]) => {
    if (!policyDraft.pendingKey) return paths;
    return paths.map((row) =>
      row.key === policyDraft.pendingKey
        ? { ...row, draft: policyDraft }
        : row,
    );
  };

  const handleAdd = () => {
    const key = `new-${Date.now()}-${pendingPaths.length}`;
    const draft = createNewDraft(key);
    setPendingPaths((prev) => [
      ...rememberCurrentPending(prev),
      { key, draft },
    ]);
    setPolicyDraft(draft);
  };

  const commitNew = (
    key: string,
    values: { displayName: string; isActive: boolean; code: string },
  ) => {
    const stored = pendingPaths.find((row) => row.key === key)?.draft;
    const base =
      policyDraft.pendingKey === key
        ? policyDraft
        : (stored ?? createNewDraft(key));
    const next: DraftPolicy = {
      ...base,
      pendingKey: key,
      displayName: values.displayName,
      isActive: values.isActive,
      code: values.code,
    };
    setPendingPaths((prev) =>
      rememberCurrentPending(prev).map((row) =>
        row.key === key ? { ...row, draft: next } : row,
      ),
    );
    setPolicyDraft(next);
  };

  const patchPending = (
    key: string,
    values: { displayName: string; isActive: boolean; code: string },
  ) => {
    setPendingPaths((prev) =>
      prev.map((row) =>
        row.key === key
          ? {
              ...row,
              draft: {
                ...row.draft,
                displayName: values.displayName,
                isActive: values.isActive,
                code: values.code,
              },
            }
          : row,
      ),
    );
  };

  const totalCount = list.length + pendingPaths.length;

  return (
    <Stack spacing={0} sx={{ width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography sx={{ color: "text.secondary", fontSize: "0.95rem" }}>
          {`${totalCount} coverage path${totalCount === 1 ? "" : "s"}`}
        </Typography>
        <Button variant="contained" onClick={handleAdd}>
          + Add new coverage path
        </Button>
      </Stack>

      {list.map((p, index) => (
        <Box key={p.escalationPolicyId}>
          {index > 0 ? <Divider sx={{ borderColor: "#e4e7ec" }} /> : null}
          <EditCoveragePath
            policy={policyWithEdits(p)}
            policyDraft={policyDraft}
            setPolicyDraft={setPolicyDraft}
            onSelectPolicy={onSelectPolicy}
            onRememberPolicy={(values) => onRememberPolicy(p, values)}
            onCommitActive={onCommitActive}
          />
        </Box>
      ))}

      {pendingPaths.map((item, index) => {
        const source =
          policyDraft.pendingKey === item.key ? policyDraft : item.draft;
        return (
          <Box key={item.key}>
            {list.length > 0 || index > 0 ? (
              <Divider sx={{ borderColor: "#e4e7ec" }} />
            ) : null}
            <EditCoveragePath
              policy={blankPolicy(source)}
              policyDraft={policyDraft}
              setPolicyDraft={setPolicyDraft}
              onSelectPolicy={onSelectPolicy}
              pendingKey={item.key}
              isNew
              onCommitNew={(values) => commitNew(item.key, values)}
              onPatchPending={(values) => patchPending(item.key, values)}
            />
          </Box>
        );
      })}
    </Stack>
  );
};
